import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Mail, MapPin, Phone, Clock, ShieldCheck, Building2, UserRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Seo } from "../components/Seo";
import { api, errMsg } from "../lib/api";
import { trackRewardlanceEvent } from "../lib/analytics";
import { useI18n } from "../i18n";
import { Wrap, Arrow, QA } from "./Public";

function Hero({ kicker, title, intro }) {
  return (
    <div className="border-b bg-muted/30">
      <Wrap className="py-16 lg:py-20">
        <p className="text-sm font-medium text-primary mb-3">{kicker}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight max-w-3xl leading-[1.15]">{title}</h1>
        {intro && <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{intro}</p>}
      </Wrap>
    </div>
  );
}

function Track({ icon: I, who, steps, cta, to, testid }) {
  return (
    <Card className="p-6 md:p-8 rounded-2xl" data-testid={testid}>
      <div className="flex items-center gap-3"><span className="rounded-xl bg-primary/10 p-2.5 text-primary"><I className="h-5 w-5" /></span><h2 className="font-display text-xl font-bold">{who}</h2></div>
      <ol className="mt-6 space-y-5">
        {steps.map(([t, d], i) => (
          <li key={t} className="flex gap-4">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-primary text-xs font-bold text-primary">{i + 1}</span>
            <div><h3 className="font-semibold">{t}</h3><p className="text-sm text-muted-foreground mt-1 leading-relaxed">{d}</p></div>
          </li>
        ))}
      </ol>
      <Button asChild className="mt-8 rounded-full" data-testid={`${testid}-cta`}><Link to={to}>{cta} <Arrow /></Link></Button>
    </Card>
  );
}

export function HowItWorks() {
  const { t } = useI18n();
  const h = t.how;
  return (
    <>
      <Hero kicker={h.kicker} title={h.title} intro={h.intro} />
      <Wrap className="py-16 grid gap-6 lg:grid-cols-2">
        <Track icon={UserRound} who={h.contributors} steps={h.userSteps} cta={h.ctaUser} to="/register" testid="track-users" />
        <Track icon={Building2} who={h.businesses} steps={h.bizSteps} cta={h.ctaBiz} to="/register?type=business" testid="track-business" />
      </Wrap>
      <Wrap className="pb-8">
        <div className="grid gap-6 md:grid-cols-3">
          {h.guarantees.map(([tt, d]) => <div key={tt} className="border-s-2 border-primary ps-5"><h3 className="font-semibold">{tt}</h3><p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d}</p></div>)}
        </div>
      </Wrap>
      <Wrap className="py-12"><div className="max-w-3xl space-y-10">{h.details.map((s) => <section key={s.h}><h2 className="font-display text-2xl font-bold">{s.h}</h2>{s.p.map((x) => <p key={x.slice(0, 40)} className="mt-3 text-muted-foreground leading-relaxed">{x}</p>)}</section>)}</div></Wrap>
    </>
  );
}

const gid = (g, i) => `faq-group-${i}`;

export function FAQ() {
  const { t, p } = useI18n();
  const f = t.faq;
  const jsonLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: f.groups.flatMap(([, qs]) => qs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } }))) };
  return (
    <>
      <Seo page="/faq" jsonLd={jsonLd} />
      <Hero kicker={f.kicker} title={f.title} intro={f.intro} />
      <Wrap className="py-16 grid gap-12 lg:grid-cols-4">
        <nav className="hidden lg:block space-y-2 text-sm sticky top-24 self-start">{f.groups.map(([g], i) => <a key={g} href={`#${gid(g, i)}`} className="block text-muted-foreground hover:text-foreground">{g}</a>)}</nav>
        <div className="lg:col-span-3 space-y-12">
          {f.groups.map(([g, qs], gi) => <div key={g} id={gid(g, gi)} className="scroll-mt-24"><h2 className="font-display text-xl font-bold mb-2">{g}</h2><QA items={qs} id={gi} /></div>)}
          <Card className="p-6 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4"><div><p className="font-semibold">{f.still}</p><p className="text-sm text-muted-foreground">{f.stillBody}</p></div><Button asChild variant="outline"><Link to={p("/contact")}>{f.contact}</Link></Button></Card>
        </div>
      </Wrap>
    </>
  );
}

export function About() {
  const { t } = useI18n();
  const a = t.about;
  return (
    <>
      <Hero kicker={a.kicker} title={a.title} intro={a.intro} />
      <Wrap className="py-16 grid gap-12 lg:grid-cols-2">
        <div className="space-y-5 text-muted-foreground leading-relaxed">
          {a.sections.map((s, i) => <div key={s.h} className="space-y-4"><h2 className={`font-display text-2xl font-bold text-foreground ${i ? "pt-4" : ""}`}>{s.h}</h2>{s.p.map((x) => <p key={x.slice(0, 40)}>{x}</p>)}</div>)}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 self-start">
          {a.values.map(([tt, d], i) => { const I = [ShieldCheck, CheckCircle2, Clock, Mail][i]; return <Card key={tt} className="p-5 rounded-xl"><I className="h-5 w-5 text-primary" /><h3 className="font-semibold mt-3">{tt}</h3><p className="text-sm text-muted-foreground mt-1">{d}</p></Card>; })}
        </div>
      </Wrap>
    </>
  );
}

function LegalDoc({ title, sections }) {
  const { t } = useI18n();
  return (
    <>
      <Hero kicker={t.legal.kicker} title={title} intro={t.legal.updated} />
      <Wrap className="py-16 grid gap-12 lg:grid-cols-4">
        <nav className="hidden lg:block space-y-2 text-sm sticky top-24 self-start">{sections.map(([h], i) => <a key={h} href={`#s${i + 1}`} className="block text-muted-foreground hover:text-foreground">{i + 1}. {h}</a>)}</nav>
        <article className="lg:col-span-3 max-w-3xl space-y-10" data-testid="legal-content">
          {sections.map(([h, ps], i) => (
            <section key={h} id={`s${i + 1}`} className="scroll-mt-24">
              <h2 className="font-display text-xl font-bold">{i + 1}. {h}</h2>
              {ps.map((x) => <p key={x.slice(0, 40)} className="mt-3 text-muted-foreground leading-relaxed">{x}</p>)}
            </section>
          ))}
        </article>
      </Wrap>
    </>
  );
}

export const Terms = () => { const { t } = useI18n(); return <LegalDoc title={t.legal.termsTitle} sections={t.legal.terms} />; };
export const Privacy = () => { const { t } = useI18n(); return <LegalDoc title={t.legal.privacyTitle} sections={t.legal.privacy} />; };

function ContactForm({ c }) {
  const [v, setV] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const f = (k) => ({ value: v[k], onChange: (e) => setV({ ...v, [k]: e.target.value }), "data-testid": `contact-${k}-input` });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/public/contact", v); trackRewardlanceEvent("contact_form_submit", { form_name: "contact_form" }); setSent(true); toast.success(c.sent); } catch (err) { toast.error(errMsg(err)); }
  };
  if (sent) return <Card className="p-8 rounded-2xl" data-testid="contact-success"><CheckCircle2 className="h-6 w-6 text-emerald-500" /><p className="mt-3 font-semibold">{c.thanks}</p><p className="text-sm text-muted-foreground mt-1">{c.reply.replace("{email}", v.email)}</p></Card>;
  return (
    <Card className="p-6 md:p-8 rounded-2xl">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="contact-form">
        <Input required placeholder={c.name} {...f("name")} />
        <Input required type="email" placeholder={c.email} {...f("email")} />
        <Input required className="sm:col-span-2" placeholder={c.subject} {...f("subject")} />
        <Textarea required rows={6} className="sm:col-span-2" placeholder={c.message} {...f("message")} />
        <Button type="submit" className="w-fit rounded-full" data-testid="contact-submit-button">{c.send}</Button>
      </form>
    </Card>
  );
}

export function Contact() {
  const { t } = useI18n();
  const c = t.contact;
  return (
    <>
      <Hero kicker={c.kicker} title={c.title} intro={c.intro} />
      <Wrap className="py-16 grid gap-10 lg:grid-cols-3">
        <div className="space-y-5">
          {c.topics.map(([tt, d], i) => { const I = [UserRound, Building2, ShieldCheck][i]; return <div key={tt} className="flex gap-4"><I className="h-5 w-5 text-primary shrink-0 mt-0.5" /><div><h2 className="font-semibold">{tt}</h2><p className="text-sm text-muted-foreground">{d}</p></div></div>; })}
          <Card className="p-5 rounded-2xl space-y-3 text-sm" data-testid="contact-office-card">
            <div className="flex gap-3"><MapPin className="h-5 w-5 text-primary shrink-0" /><address className="not-italic"><span className="font-semibold">{c.office}</span><br />Level 1, One Mayfair Place<br />Mayfair, London, W1J 8AJ<br />United Kingdom</address></div>
            <a href="tel:+447988975205" className="flex gap-3 hover:text-primary" data-testid="contact-phone-link"><Phone className="h-5 w-5 text-primary shrink-0" /><span dir="ltr">+44 7988 975 205</span></a>
            <a href="mailto:info@rewardlance.com" className="flex gap-3 hover:text-primary" data-testid="contact-email-link"><Mail className="h-5 w-5 text-primary shrink-0" />info@rewardlance.com</a>
            <p className="flex gap-3 text-muted-foreground"><Clock className="h-5 w-5 text-primary shrink-0" />{c.hours}</p>
          </Card>
          <p className="text-sm text-muted-foreground">{c.signedIn}</p>
        </div>
        <div className="lg:col-span-2"><ContactForm c={c} /></div>
      </Wrap>
    </>
  );
}
